import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/authStore';
import { authApi } from '@/lib/api/auth';
import { LogoSVG } from '@/design-system/LogoSVG';

/**
 * Traduz a falha da chamada de login.
 *
 * Antes tudo caía em "E-mail ou senha inválidos", inclusive servidor fora do ar
 * e bloqueio de CORS — casos em que a senha nunca chegou a ser conferida. Isso
 * manda o usuário tentar de novo a credencial certa indefinidamente, e esconde
 * a falha real de quem for investigar.
 */
function mensagemDeErro(err: any): string {
  const status = err?.response?.status;

  // Sem `response` a requisição não chegou a ter resposta: rede, DNS, CORS ou
  // servidor indisponível.
  if (!err?.response) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão. Se o problema persistir, o serviço pode estar fora do ar.';
  }

  if (status === 401 || status === 400) {
    const msg = err.response.data?.message;
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg)) return msg.join(' · ');
    return 'E-mail ou senha inválidos.';
  }

  if (status >= 500) {
    return `O servidor falhou ao processar o login (erro ${status}). Isso não é problema da sua senha.`;
  }

  return `Não foi possível entrar (erro ${status}).`;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Destino pretendido antes do redirecionamento do RequireAuth
  const destino = (location.state as { from?: string } | null)?.from || '/hub';
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Presença de tempToken é o que decide se a tela está no passo 2 (MFA).
  // `POST /auth/login` (apps/api/src/core/auth/auth.service.ts:129-146) só
  // devolve token final quando a conta é de fator único; com MFA ligado, ele
  // devolve { mfaRequired, tempToken } e a sessão só fecha depois de um
  // segundo POST a /auth/mfa com { tempToken, code } (authApi.verifyMfa).
  const [tempToken, setTempToken] = useState('');
  const [codigoMfa, setCodigoMfa] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !senha) {
      setError('Preencha e-mail e senha.');
      return;
    }

    setLoading(true);
    try {
      const data = await authApi.login(email, senha);
      setLoading(false);

      if (data.token && data.user) {
        login(data.user, data.token);
        navigate(destino, { replace: true });
        return;
      }

      if (data.mfaRequired && data.tempToken) {
        setTempToken(data.tempToken);
      }
    } catch (err: any) {
      setLoading(false);
      setError(mensagemDeErro(err));
    }
  };

  const handleSubmitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!codigoMfa) {
      setError('Informe o código do segundo fator.');
      return;
    }

    setLoading(true);
    try {
      const data = await authApi.verifyMfa(tempToken, codigoMfa);
      setLoading(false);
      login(data.user, data.token);
      navigate(destino, { replace: true });
    } catch (err: any) {
      setLoading(false);
      setError(mensagemDeErro(err));
    }
  };

  // Sessão já ativa não precisa passar pelo formulário
  if (isAuthenticated) {
    return <Navigate to={destino} replace />;
  }

  return (
    <div className="login" role="main">
      {/* Lado esquerdo — identidade visual */}
      <div className="l" aria-hidden="true">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <LogoSVG size={38} color="#F58406" segment="all" />
          <span className="wordmark" style={{ fontSize: 22 }}>Eleg360</span>
        </div>
        <div>
          <h1 style={{ marginTop: 24, fontSize: 36, lineHeight: 1.15 }}>
            Visão completa do mandato.
          </h1>
          <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
            Do WhatsApp com o apoiador no interior ao analytics das votações no TRE. Uma plataforma, vários módulos: você ativa o que precisa e enxerga o todo.
          </p>
          <div className="mods" style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>Eleg · ativo</span>
            <span>Gabinete360 · ativo</span>
            <span>Atendimento · em breve</span>
          </div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.55, marginTop: 'auto' }}>
          Multi-tenant · LGPD by design · Auditoria estrutural · PWA offline
        </div>
      </div>

      {/* Lado direito — formulário de autenticação */}
      <div className="r">
        {!tempToken ? (
          <form onSubmit={handleSubmit} noValidate aria-label="Formulário de login">
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>Entrar no Eleg360</h2>

            <div className="field">
              <label htmlFor="login-email">E-mail institucional</label>
              <input
                id="login-email"
                type="email"
                className="in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
              />
            </div>
            <div className="field">
              <label htmlFor="login-senha">Senha</label>
              <input
                id="login-senha"
                type="password"
                className="in"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div className="note crit" role="alert" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn pri"
              disabled={loading}
              id="login-submit-btn"
              style={{ width: '100%', padding: '11px', fontSize: 14 }}
            >
              {loading ? 'Aguarde...' : 'Entrar'}
            </button>

            <p style={{ fontSize: 12, color: 'var(--ink3)', textAlign: 'center', marginTop: 14 }}>
              Acesso restrito · sessões auditadas
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmitMfa} noValidate aria-label="Formulário de confirmação do segundo fator">
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>Entrar no Eleg360</h2>

            <div className="field">
              <label htmlFor="login-mfa">Código MFA</label>
              <input
                id="login-mfa"
                className="in mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigoMfa}
                onChange={(e) => setCodigoMfa(e.target.value)}
                autoFocus
                required
              />
            </div>

            {error && (
              <div className="note crit" role="alert" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn pri"
              disabled={loading}
              id="login-mfa-submit-btn"
              style={{ width: '100%', padding: '11px', fontSize: 14 }}
            >
              {loading ? 'Aguarde...' : 'Confirmar'}
            </button>

            <p style={{ fontSize: 12, color: 'var(--ink3)', textAlign: 'center', marginTop: 14 }}>
              Acesso restrito · sessões auditadas
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
